import {
    GridActionsCell,
    GridActionsCellItem,
    type GridRenderCellParams,
    useGridApiContext,
} from '@mui/x-data-grid';
import type { BulkEditingContextValue, UnsavedChanges } from '../../utils/BulkEditHelper.tsx';
import { Restore } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import type { ReactElement } from 'react';
import {useSelector} from "react-redux";

interface ExtraAction {
    icon: ReactElement;
    label: string;
    onClick: () => void;
    disabled?: boolean;
    tooltipText?: string;
    showInMenu?: boolean;
}

export default function BulkEditActionsCell(
    props: GridRenderCellParams & {
        context: BulkEditingContextValue | null;
        extraActions?: ExtraAction[];
        onRowDiscard?: (rowId: GridRenderCellParams['id']) => void;
        showRestoreAction?: boolean;
    }
) {
    const { id, context, extraActions = [], onRowDiscard, showRestoreAction = true } = props;
    const apiRef = useGridApiContext();
    if (!context) {
        return null;
    }
    const { unsavedChangesRef, setHasUnsavedRows } = context;

    const isActual = useSelector((state) => state.details.isActual);

    const handleClickEvent = (unsavedChanges: UnsavedChanges) => {
        apiRef.current?.updateRows([unsavedChanges.rowsBeforeChange[id]]);
        delete unsavedChanges.rowsBeforeChange[id];
        delete unsavedChanges.unsavedRows[id];
        setHasUnsavedRows(Object.keys(unsavedChanges.unsavedRows).length > 0);
        onRowDiscard?.(id);
    };

    return (
        // MUI grid gets upset by the tooltip, but we want to display the tooltip when the cell item is disabled, so use suppressChildrenValidation to work around it
        <GridActionsCell {...props} suppressChildrenValidation>
            {extraActions.map((action) => (
                <Tooltip key={action.label} title={action.tooltipText ?? action.label}>
                    <span>
                        <GridActionsCellItem
                            showInMenu={action.showInMenu ?? false}
                            icon={action.icon}
                            label={action.label}
                            disabled={action.disabled}
                            onClick={action.onClick}
                        />
                    </span>
                </Tooltip>
            ))}

            {showRestoreAction && (
                <Tooltip
                    title={
                        !isActual
                            ? 'Discard changes'
                            : 'Editing is disabled for actual treatments or activities'
                    }
                >
                    <span>
                        <GridActionsCellItem
                            icon={<Restore />}
                            label={
                                !isActual
                                    ? 'Discard changes'
                                    : 'Editing is disabled for actual treatments or activities'
                            }
                            disabled={
                                unsavedChangesRef?.current?.unsavedRows[id] === undefined || isActual
                            }
                            onClick={() => {
                                if (unsavedChangesRef?.current) {
                                    handleClickEvent(unsavedChangesRef?.current);
                                }
                            }}
                        />
                    </span>
                </Tooltip>
            )}
        </GridActionsCell>
    );
}
